import React from "react";
import { Quote } from "lucide-react";
import OptimizedImage from "@/components/OptimizedImage";

export const JournalImage: React.FC<{ src: string; alt: string; caption?: string; fullWidth?: boolean }> = ({ src, alt, caption, fullWidth }) => (
  <div className={`my-12 ${fullWidth ? "-mx-4 md:-mx-20" : ""}`}>
    <div className="rounded-sm shadow-lg overflow-hidden">
      <OptimizedImage
        src={src}
        alt={alt}
        className="w-full"
        contentImage={true}
        hideLoadingSpinner={true}
        priority={false}
      />
    </div>
    {caption && (
      <p className="mt-3 text-center text-sm text-gray-500 font-light italic font-playfair tracking-wide">
        {caption}
      </p>
    )}
  </div>
);

export const JournalQuote: React.FC<{ children: React.ReactNode; author?: string }> = ({ children, author }) => (
  <div className="my-16 relative py-8 px-6 md:px-12 text-center">
    <Quote className="w-12 h-12 text-alpine-gold/20 absolute top-0 left-1/2 -translate-x-1/2" />
    <blockquote className="text-2xl md:text-3xl font-playfair italic text-gray-800 leading-relaxed relative z-10">
      "{children}"
    </blockquote>
    {author && (
      <cite className="block mt-6 text-sm uppercase tracking-widest text-alpine-gold font-medium not-italic">
        — {author}
      </cite>
    )}
  </div>
);

export const JournalCallout: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="my-10 p-8 bg-gray-50 border border-gray-100 rounded-sm relative overflow-hidden">
    <div className="absolute top-0 left-0 w-1 h-full bg-alpine-gold" />
    <h4 className="text-lg font-playfair font-bold text-gray-900 mb-3">{title}</h4>
    <div className="text-gray-600 leading-relaxed font-light">
      {children}
    </div>
  </div>
);

export const JournalDivider: React.FC = () => (
  <div className="my-16 flex justify-center items-center gap-4">
    <div className="h-[1px] w-20 bg-gray-200" />
    <div className="w-2 h-2 rotate-45 bg-alpine-gold" />
    <div className="h-[1px] w-20 bg-gray-200" />
  </div>
);

export const JournalList: React.FC<{ items: string[] }> = ({ items }) => (
  <ul className="my-8 space-y-3">
    {items.map((item, index) => (
      <li key={index} className="flex items-start">
        <span className="inline-block w-2 h-2 rounded-full bg-alpine-gold mt-2 mr-3 flex-shrink-0" />
        <span className="text-gray-700 font-light leading-relaxed">{item}</span>
      </li>
    ))}
  </ul>
);

import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

export const JournalCTA: React.FC<{ href: string; children: React.ReactNode }> = ({ href, children }) => (
  <div className="my-16 flex justify-center">
    <Button
      asChild
      className="bg-alpine-gold hover:bg-swiss-red text-white font-playfair uppercase tracking-widest text-lg px-8 py-6 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
    >
      <Link to={href}>
        {children}
      </Link>
    </Button>
  </div>
);

export const JournalTable: React.FC<{ headers: string[]; rows: string[][] }> = ({ headers, rows }) => (
  <div className="my-12 overflow-x-auto">
    <table className="w-full border-collapse bg-white shadow-sm rounded-sm overflow-hidden">
      <thead>
        <tr className="bg-pine-green text-white">
          {headers.map((header, i) => (
            <th key={i} className="py-4 px-6 text-left font-playfair tracking-wide border-b border-pine-green/80">
              {header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} className={i % 2 === 0 ? "bg-gray-50" : "bg-white"}>
            {row.map((cell, j) => (
              <td key={j} className="py-4 px-6 border-b border-gray-100 text-gray-600 font-light">
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
